import { Globe, Lock, MessageCircle, Users, MoreVertical } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { getImageUrl } from '../../utils/imageUrl';
import { formatDate } from '../../utils/formatDate';
import useModal from '../../hooks/useModal';
import { IRoom } from '../../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import RoomDeleteDialog from './RoomDeleteDialog';
import RoomAssignAdminDialog from './RoomAssignAdminDialog';

interface ISpaceRoomSectionProps {
  rooms: (IRoom & { messageCount?: number; memberCount?: number })[];
  /**
   * Space owner, shown in the Admin column when a room has no room-level
   * admin (SITOSP-381): the owner administers every room of the space
   * implicitly, so a room created without picking an admin is theirs.
   */
  spaceOwner?: {
    id?: string;
    username: string;
    displayName: string;
    avatar?: string | null;
  } | null;
  onNavigateRoom: (roomId: string) => void;
  onViewMessages?: (roomId: string) => void;
  onViewMembers?: (roomId: string) => void;
}

const SpaceRoomSection = ({
  rooms,
  spaceOwner,
  onNavigateRoom,
  onViewMessages,
  onViewMembers,
}: ISpaceRoomSectionProps) => {
  const { t } = useTranslation();
  const deleteDialog = useModal<IRoom>();
  const assignAdminDialog = useModal<IRoom>();

  return (
    <>
      <Card className="gap-0 overflow-hidden">
        <Table style={{ minWidth: '700px' }}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('spaces.room_name')}</TableHead>
              <TableHead>{t('spaces.admin')}</TableHead>
              <TableHead>{t('spaces.type')}</TableHead>
              <TableHead>{t('spaces.member_count')}</TableHead>
              <TableHead>{t('spaces.message_count')}</TableHead>
              <TableHead>{t('common.created_at')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead className="text-right">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rooms.map((room) => (
              <TableRow
                key={room.id}
                onClick={() => onNavigateRoom(room.id)}
                className="cursor-pointer"
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white overflow-hidden shrink-0">
                      {(room as any).avatar ? (
                        <img
                          src={getImageUrl((room as any).avatar)!}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <MessageCircle className="w-4 h-4" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {room.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {(() => {
                    const admins =
                      room.roomMembers?.filter((m) => m.role === 'admin') || [];
                    const firstAdmin = admins[0];
                    const shown = firstAdmin
                      ? {
                          avatar: firstAdmin.user?.avatar,
                          name:
                            firstAdmin.user?.displayName || firstAdmin.userId,
                        }
                      : spaceOwner
                        ? {
                            avatar: spaceOwner.avatar,
                            name: spaceOwner.displayName || spaceOwner.username,
                          }
                        : null;

                    if (!shown) return '—';

                    const { avatar, name } = shown;

                    return (
                      <div className="flex items-center gap-2">
                        {avatar ? (
                          <img
                            src={getImageUrl(avatar)!}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm text-foreground">{name}</span>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {room.isPrivate ? (
                    <Badge
                      variant="outline"
                      className="gap-1 text-purple-600 border-0 bg-purple-50"
                    >
                      <Lock className="w-3 h-3" />
                      {t('chatroom.private_room')}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="gap-1 text-blue-600 border-0 bg-blue-50"
                    >
                      <Globe className="w-3 h-3" />
                      {t('chatroom.public_room')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  <Users className="w-4 h-4 inline" />{' '}
                  {(room as any).effectiveMemberCount ??
                    room.memberCount ??
                    room.roomMembers?.length ??
                    0}
                </TableCell>
                <TableCell className="text-sm">
                  {(room as any).messageCount ?? '—'}
                </TableCell>
                <TableCell className="text-sm text-gray-500">
                  {formatDate(room.createdAt)}
                </TableCell>
                <TableCell>
                  <Badge variant="success" className="rounded-full">
                    {t('common.active')}
                  </Badge>
                </TableCell>
                <TableCell
                  className="text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[160px]">
                      <DropdownMenuItem onClick={() => onNavigateRoom(room.id)}>
                        {t('spaces.view_detail')}
                      </DropdownMenuItem>
                      {onViewMessages && (
                        <DropdownMenuItem
                          onClick={() => onViewMessages(room.id)}
                        >
                          {t('chatroom.view_messages')}
                        </DropdownMenuItem>
                      )}
                      {onViewMembers && (
                        <DropdownMenuItem
                          onClick={() => onViewMembers(room.id)}
                        >
                          {t('spaces.view_members')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => assignAdminDialog.open(room)}
                      >
                        {t('spaces.assign_admin')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => deleteDialog.open(room)}
                        className="text-red-600 focus:text-red-600"
                      >
                        {t('spaces.delete_room')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {rooms.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="px-6 py-12 text-center text-sm text-gray-400"
                >
                  {t('spaces.no_rooms')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Room action dialogs */}
      <RoomDeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        room={deleteDialog.selectedItem}
      />
      <RoomAssignAdminDialog
        isOpen={assignAdminDialog.isOpen}
        onClose={assignAdminDialog.close}
        room={assignAdminDialog.selectedItem}
      />
    </>
  );
};

export default SpaceRoomSection;
